import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStatusChipSx } from '../../constants/statusColors';
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../services/api';
import { mapCommitteeFromApi, formatDisplayDate } from '../../utils/apiMappers';

// Import tab components
import OverviewTab from './tabs/OverviewTab';
import MembersTab from './tabs/MembersTab';
import MeetingsTab from './tabs/MeetingsTab';
import DocumentsTab from './tabs/DocumentsTab';
import ActivityTab from './tabs/ActivityTab';

// Import modals
import AddMemberModal from '../../components/committees/AddMemberModal';

const CommitteeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [committee, setCommittee] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCommitteeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCommitteeDetails = async () => {
    try {
      setLoading(true);

      const response = await csAdminApi.getCommitteeById(id);
        const data = mapCommitteeFromApi(response.data?.data);
        if (!data) {
          setError('Committee not found');
          return;
        }
        setCommittee(data);
        setError('');
    } catch (err) {
      console.error('Error fetching committee details:', err);
      setError('Failed to load committee details');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddMember = () => {
    setSnackbar({
      open: true,
      message: 'Member added successfully to the committee',
      severity: 'success',
    });
    fetchCommitteeDetails();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !committee) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Alert severity="error">{error || 'Committee not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/committees')} sx={{ mt: 2 }}>
          Back to Committees
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/committees')}
          sx={{ mb: 2 }}
        >
          Back to Committees
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" component="h1">
                {committee.name}
              </Typography>
              <Chip
                label={committee.status}
                sx={{
                  ...getStatusChipSx(committee.status),
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                }}
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {committee.type} • Created: {formatDisplayDate(committee.createdDate)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddMemberOpen(true)}
              sx={{ borderRadius: '6px' }}
            >
              Add Member
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/committees/${id}/edit`)}
              sx={{ borderRadius: '6px' }}
            >
              Edit Committee
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="OVERVIEW" />
          <Tab label={`MEMBERS (${committee.members?.length || 0})`} />
          <Tab label={`MEETINGS (${committee.meetings?.length || 0})`} />
          <Tab label={`DOCUMENTS (${committee.documents?.length || 0})`} />
          <Tab label="ACTIVITY" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {activeTab === 0 && <OverviewTab committee={committee} />}
        {activeTab === 1 && <MembersTab committee={committee} onRefresh={fetchCommitteeDetails} />}
        {activeTab === 2 && <MeetingsTab committee={committee} onRefresh={fetchCommitteeDetails} />}
        {activeTab === 3 && <DocumentsTab committee={committee} onRefresh={fetchCommitteeDetails} />}
        {activeTab === 4 && <ActivityTab committee={committee} />}
      </Box>

      {/* Add Member Modal */}
      <AddMemberModal
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        onAdd={handleAddMember}
        committeeId={committee.id}
        committeeName={committee.name}
        existingMembers={committee.members || []}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommitteeDetails;
